// /src/components/LoginV3.js
// Componente Modal Login V3 Editorial
// Sistema de diseño SFS 2.0

import React, { useState } from "react";
import { Link } from "gatsby";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useI18next } from "gatsby-plugin-react-i18next";
import { EMAIL_FUNCTIONS_BASE, emailRequestHeaders } from "../utils/emailFunctions";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { useSchool } from "../context/SchoolContext";

// --- Icono de Google ---
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LoginV3 = ({ onClose }) => {
  const { t } = useTranslation();
  const { navigate } = useI18next();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const { loginSource, switchToSignup, clearReturnUrl } = useModal();
  const school = useSchool();
  const schoolId = school?.ai?.schoolId || 'sfs';

  const getDestination = () => {
    const stored = typeof window !== 'undefined' ? sessionStorage.getItem('loginReturnUrl') : null;
    return stored || '/fluency-compass';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError(t("auth.login.errors.emptyFields"));
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      const destination = getDestination();
      clearReturnUrl();
      navigate(destination);
      onClose();
    } catch (err) {
      console.error("Login Error:", err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError(t("auth.login.errors.invalidCredentials"));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t("auth.login.errors.tooManyRequests"));
      } else {
        setError(t("auth.login.errors.generalError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const { user, isNewUser } = await loginWithGoogle();

      // If this is a new user, treat it as a signup
      if (isNewUser) {
        const firstName = user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'Student';
        const lastName = user.displayName?.split(' ').slice(1).join(' ') || '';

        // Send welcome email
        try {
          await fetch(`${EMAIL_FUNCTIONS_BASE}/sendWelcomeEmail`, {
            method: 'POST',
            headers: await emailRequestHeaders(),
            body: JSON.stringify({
              email: user.email,
              firstName,
              lastName,
              schoolId,
            })
          });
        } catch (emailError) {
          console.error("Welcome email error:", emailError);
        }

        // New users always go to fluency compass (welcome modal shows there)
        clearReturnUrl();
        navigate('/fluency-compass');
      } else {
        // Existing users: go to returnUrl if available, otherwise fluency compass
        const destination = getDestination();
        clearReturnUrl();
        navigate(destination);
      }

      onClose();
    } catch (err) {
      console.error("Google Sign-in Error:", err);
      setError(t("auth.login.errors.googleError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-[#0F172A]/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 z-50 overflow-y-auto"
        onClick={onClose}
      >
        {/* Modal Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden my-auto sm:my-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative top accent */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#E76F51]/40 via-[#E76F51]/40 to-[#0F172A]/40" />

          {/* Close Button */}
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded-xl text-brand-text/40 hover:text-[#0F172A] hover:bg-brand-light transition-all duration-200 z-10"
            onClick={onClose}
            aria-label={t("auth.login.closeButton")}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-5 md:p-6 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 mb-2">
                <img
                  src="/images/Logo/Logo Icon SFS.png"
                  alt="SFS"
                  className="w-12 h-12"
                />
              </div>
              <h2 className="font-display text-xl md:text-2xl font-bold text-[#0F172A] mb-0.5 leading-tight">
                {t("auth.login.title")}
              </h2>
              <p className="font-sans text-xs md:text-sm text-brand-text/60">
                {t("auth.login.subtitle").split("<fluency>")[0]}
                <span className="text-[#E76F51] font-semibold">
                  {t("auth.login.subtitle").split("<fluency>")[1]?.split("</fluency>")[0]}
                </span>
                {t("auth.login.subtitle").split("</fluency>")[1]}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg"
              >
                <p className="font-sans text-xs md:text-sm text-red-700 text-center">{error}</p>
              </motion.div>
            )}

            {/* Google Button */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-white border-2 border-[#0F172A]/10 rounded-lg font-sans font-semibold text-sm md:text-base text-[#0F172A] hover:border-[#0F172A]/30 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              <GoogleIcon />
              <span>{t("auth.login.googleButton")}</span>
            </button>

            {/* Divider */}
            <div className="relative mb-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-brand-text/10"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-2.5 bg-white font-sans text-xs text-brand-text/50">
                  {t("auth.login.divider")}
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-3">
              {/* Email */}
              <div>
                <label
                  htmlFor="login-email"
                  className="block font-sans text-xs md:text-sm font-semibold text-[#0F172A] mb-1"
                >
                  {t("auth.login.emailLabel")}
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full px-3.5 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm md:text-base text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200"
                  placeholder={t("auth.login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label
                    htmlFor="login-password"
                    className="block font-sans text-xs md:text-sm font-semibold text-[#0F172A]"
                  >
                    {t("auth.login.passwordLabel")}
                  </label>
                  <Link
                    to="/forgot-password"
                    onClick={onClose}
                    className="font-sans text-xs text-[#E76F51] hover:underline"
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="w-full px-3.5 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm md:text-base text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200 pr-10"
                    placeholder={t("auth.login.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text/40 hover:text-[#0F172A] transition-colors"
                    aria-label={t("auth.login.showPassword")}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[#E76F51] text-white font-sans font-semibold text-sm md:text-base rounded-lg hover:bg-[#d4614a] shadow-lg shadow-[#E76F51]/25 hover:shadow-xl hover:shadow-[#E76F51]/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t("auth.login.submitting")}</span>
                  </>
                ) : (
                  <>
                    <span>{t("auth.login.submitButton")}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Sign Up Link */}
            <p className="mt-4 text-center font-sans text-xs md:text-sm text-brand-text/60">
              {t("auth.login.noAccount")}{" "}
              <button
                type="button"
                onClick={() => switchToSignup('login-to-signup')}
                className="font-semibold text-[#E76F51] hover:underline bg-transparent border-0 cursor-pointer"
              >
                {t("auth.login.signupLink")}
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoginV3;
